import { forwardRef } from 'react';
import useInView from '../../hooks/useInView.js';

/**
 * ProcessStep — one branch of the Process spine.
 *
 * Layout: 3-col grid on md+ (content | spine | spacer) — sides alternate
 * via the `side` prop. Collapses to left-spine + single-col on mobile.
 *
 * Props:
 *   index     — 1-based phase number (padded to 2 digits for display)
 *   title     — phase heading
 *   body      — short description paragraph
 *   items     — string[] of deliverables
 *   side      — 'left' | 'right' — which branch the content sits on
 *   isActive  — true when this phase is the viewport-centre phase
 */
const ProcessStep = forwardRef(function ProcessStep(
  { index, title, body, items = [], side, isActive },
  ref
) {
  // Entry animation fires when this step scrolls into view, not at mount —
  // scrolling provides the stagger, so the delay is a constant beat.
  const [contentRef, contentIn] = useInView({
    rootMargin: '0px 0px -60px 0px',
    threshold: 0.15,
  });

  const num = String(index).padStart(2, '0');
  const isLeft = side === 'left';

  const contentCls = [
    'flex flex-col gap-2.5',
    'col-start-2',
    isLeft
      ? 'md:col-start-1 md:text-right md:items-end'
      : 'md:col-start-3 md:text-left md:items-start',
    contentIn
      ? isLeft
        ? 'animate-phase-in-left'
        : 'animate-phase-in-right'
      : 'opacity-0',
  ].join(' ');

  return (
    <article
      ref={ref}
      className={[
        'relative grid items-center py-[18px]',
        // Mobile: narrow spine gutter + content
        'grid-cols-[40px_1fr]',
        // Desktop: content | spine | spacer
        'md:grid-cols-[1fr_120px_1fr]',
      ].join(' ')}
    >
      <div ref={contentRef} className={contentCls} style={{ animationDelay: '150ms' }}>
        <span
          aria-hidden="true"
          className="font-display font-bold leading-[0.88] tracking-[-0.06em] select-none transition-all duration-700"
          style={{
            fontSize: 'clamp(56px, 7vw, 100px)',
            WebkitTextStroke: `1.25px ${isActive ? '#3B82F6' : 'rgba(255,255,255,0.18)'}`,
            color: 'transparent',
          }}
        >
          {num}
        </span>
        <h3 className="text-h3 font-display font-semibold text-ink-50 m-0">{title}</h3>
        <p className="text-body-sm text-ink-200 m-0 max-w-[42ch]">{body}</p>
        <ul
          className={[
            'm-0 p-0 list-none flex flex-col gap-1.5 font-mono text-mono-sm text-ink-300',
          ].join(' ')}
        >
          {items.map((item) => (
            <li
              key={item}
              className={[
                'flex items-center gap-2.5',
                isLeft ? 'md:flex-row-reverse' : '',
              ].join(' ')}
            >
              <span
                className={[
                  'w-1 h-1 rounded-full bg-brand-blue-bright flex-shrink-0 transition-all duration-500',
                  isActive ? 'opacity-100 scale-100 shadow-step-dot-glow' : 'opacity-50 scale-[0.6]',
                ].join(' ')}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Spine marker */}
      <div
        aria-hidden="true"
        className="col-start-1 md:col-start-2 justify-self-center relative z-[3] w-3.5 h-3.5"
      >
        <span
          className={[
            'absolute inset-0 rounded-full border-[1.5px] transition-all duration-500',
            isActive
              ? 'bg-brand-blue-bright border-brand-blue-bright shadow-step-ring'
              : 'bg-surface-alt border-white/[.14]',
          ].join(' ')}
        />
        {isActive && (
          <span className="absolute inset-0 rounded-full border-[1.5px] border-brand-blue-bright animate-spine-pulse" />
        )}
      </div>

      {/* Desktop-only spacer that parks the opposite column */}
      <div
        aria-hidden="true"
        className={[
          'hidden md:block',
          isLeft ? 'md:col-start-3' : 'md:col-start-1',
        ].join(' ')}
      />
    </article>
  );
});

export default ProcessStep;
